// 게스트 사용자 크레딧 관리 시스템

const DAILY_CREDITS = 100000; // 하루 제공 크레딧
const CREDITS_KEY = 'otj_guest_credits';
const LAST_RESET_KEY = 'otj_credits_last_reset';

/**
 * 크레딧 계산 함수
 * @param {string} text - 텍스트
 * @param {string} mode - 모드 (proofread/copyedit)
 * @param {boolean} includereasons - 수정 이유 포함 여부
 * @returns {number} 필요한 크레딧 수
 */
export const calculateCredits = (text, mode, includereasons) => {
  const textLength = text ? text.length : 0;
  
  let baseMultiplier;
  let reasonsMultiplier;
  
  if (mode === 'proofread') {
    baseMultiplier = 1;
    reasonsMultiplier = 2;
  } else if (mode === 'copyedit') {
    baseMultiplier = 2;
    reasonsMultiplier = 4;
  } else {
    // 기본값 (미래 확장용)
    baseMultiplier = 1;
    reasonsMultiplier = 3;
  }
  
  const multiplier = includereasons ? reasonsMultiplier : baseMultiplier;
  const credits = textLength * multiplier;
  
  console.log('[Credits] 크레딧 계산:', {
    textLength,
    mode,
    includereasons,
    multiplier,
    totalCredits: credits
  });
  
  return credits;
};

/**
 * 현재 크레딧 조회
 * @returns {number} 현재 크레딧 수
 */
export const getCurrentCredits = () => {
  try {
    // 하루가 지났는지 확인하고 리셋
    checkAndResetDailyCredits();
    
    const credits = localStorage.getItem(CREDITS_KEY);
    const currentCredits = credits ? parseInt(credits, 10) : DAILY_CREDITS;
    
    console.log('[Credits] 현재 크레딧:', currentCredits);
    return currentCredits;
  } catch (error) {
    console.error('[Credits] 크레딧 조회 오류:', error);
    return DAILY_CREDITS;
  }
};

/**
 * 크레딧 차감
 * @param {number} amount - 차감할 크레딧 수
 * @returns {boolean} 차감 성공 여부
 */
export const deductCredits = (amount) => {
  try {
    const currentCredits = getCurrentCredits();
    
    if (currentCredits < amount) {
      console.warn('[Credits] 크레딧 부족:', { current: currentCredits, required: amount });
      return false;
    }
    
    const newCredits = currentCredits - amount;
    localStorage.setItem(CREDITS_KEY, newCredits.toString());
    
    console.log('[Credits] 크레딧 차감:', {
      previous: currentCredits,
      deducted: amount,
      remaining: newCredits
    });
    
    return true;
  } catch (error) {
    console.error('[Credits] 크레딧 차감 오류:', error);
    return false;
  }
};

/**
 * 크레딧 사용 가능 여부 확인
 * @param {number} requiredCredits - 필요한 크레딧 수
 * @returns {boolean} 사용 가능 여부
 */
export const canUseCredits = (requiredCredits) => {
  const currentCredits = getCurrentCredits();
  return currentCredits >= requiredCredits;
};

/**
 * 하루가 지났는지 확인하고 크레딧 리셋
 */
const checkAndResetDailyCredits = () => {
  try {
    const lastReset = localStorage.getItem(LAST_RESET_KEY);
    const now = new Date();
    const today = now.toDateString(); // "Mon Dec 25 2023" 형태
    
    if (lastReset !== today) {
      // 새로운 날이므로 크레딧 리셋
      localStorage.setItem(CREDITS_KEY, DAILY_CREDITS.toString());
      localStorage.setItem(LAST_RESET_KEY, today);
      
      console.log('[Credits] 일일 크레딧 리셋:', {
        previousReset: lastReset,
        currentDate: today,
        newCredits: DAILY_CREDITS
      });
    }
  } catch (error) {
    console.error('[Credits] 일일 리셋 확인 오류:', error);
  }
};

/**
 * 크레딧을 특정 값으로 설정 (테스트용)
 * @param {number} amount - 설정할 크레딧 수
 */
export const setCredits = (amount) => {
  try {
    localStorage.setItem(CREDITS_KEY, amount.toString());
    console.log('[Credits] 크레딧 설정:', amount);
  } catch (error) {
    console.error('[Credits] 크레딧 설정 오류:', error);
  }
};

/**
 * 크레딧을 10,000으로 초기화 (테스트용)
 */
export const resetCreditsToDefault = () => {
  setCredits(DAILY_CREDITS);
  console.log('[Credits] 크레딧을 기본값으로 초기화:', DAILY_CREDITS);
};

/**
 * 크레딧 관련 정보 조회
 * @returns {Object} 크레딧 정보
 */
export const getCreditInfo = () => {
  const currentCredits = getCurrentCredits();
  const lastReset = localStorage.getItem(LAST_RESET_KEY);
  
  return {
    currentCredits,
    dailyLimit: DAILY_CREDITS,
    lastReset,
    nextResetTime: getNextResetTime()
  };
};

/**
 * 다음 리셋 시간 계산
 * @returns {Date} 다음 리셋 시간
 */
const getNextResetTime = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0); // 자정으로 설정
  return tomorrow;
};

// 테스트용 전역 함수 (개발 중에만 사용)
if (typeof window !== 'undefined') {
  window.resetOTJCredits = resetCreditsToDefault;
  window.setOTJCredits = setCredits;
  window.getOTJCreditInfo = getCreditInfo;
} 